import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { WProductLookView } from '@zola/svc-web-api-ts-client';
import type { CartItemType } from '@zola-helpers/client/dist/es/@types/cart';
import { excludeMobile, mobileOnly } from '../../styles/emotion/mixins/responsive';
import TagV2, { TagV2Variant, TagV2Size } from '../Tag/TagV2';
import { useDGC } from '../DGCCarousel/useDGC';
import { PlusIcon } from '../SvgIconsV3/Plus';
import { ButtonV3 } from '../ButtonV3';
import { ButtonV3Props } from '../ButtonV3/ButtonV3';
import { CheckIcon } from '../SvgIconsV3/Check';
import { LinkV2 } from '../LinkV2';

import CompactCurrencyFilter from './utils/PricingUtils';

import {
  DGCsUpsellContainer,
  DGCCopyContainer,
  EnvelopeIconContainer,
  DGCDek,
  DGCHed,
  Bold,
  DGCImageWithRotation,
  DGCInfo,
  AddToCartContainer,
  AddedToCart,
} from './DGCsUpsell.styles';

const getDGCPrice = (dgc: ValidDGC) => {
  // DGC min and max is the same
  return CompactCurrencyFilter((dgc.price_range?.min_price || 399) / 100);
};

/* eslint-disable jsx-a11y/accessible-emoji */
const DGCCopy = ({ dgc, isDgcV5 }: { dgc: ValidDGC; isDgcV5: boolean }) => (
  <DGCCopyContainer>
    {!isDgcV5 && (
      <TagV2 variant={TagV2Variant.GRAY} size={TagV2Size.SMALL}>
        Optional
      </TagV2>
    )}
    <DGCHed>
      <Bold>Make your gift stand out with a personalized e-card</Bold>
      <EnvelopeIconContainer role="img" aria-label="envelope emoji">
        💌
      </EnvelopeIconContainer>{' '}
      (+{getDGCPrice(dgc)})
    </DGCHed>
    <DGCDek>Choose a design and add your message at checkout.</DGCDek>
  </DGCCopyContainer>
);

type ValidImage = { aspect_ratios: { '1x1': { medium: string } } };
type ValidDGC = WProductLookView & {
  images: [ValidImage, ...ValidImage[]];
  id: string;
};

/** Get Preview image URL for a DGC. */
function getDGCPreviewImageUrl(dgc: ValidDGC): string;
function getDGCPreviewImageUrl(dgc: WProductLookView): string | null;
function getDGCPreviewImageUrl(dgc: WProductLookView): string | null {
  const { images } = dgc;
  return images?.[0]?.aspect_ratios?.['1x1']?.medium || null;
}
/** Does the DGC have a preview image and an ID? */
const isValidDGC = (dgc: WProductLookView): dgc is ValidDGC => {
  return Boolean(getDGCPreviewImageUrl(dgc)) && Boolean(dgc.id);
};

/** Renders the preview image of the DGC offered in the upsell */
const DGCImage = ({ digitalGreetingCard }: { digitalGreetingCard: ValidDGC }) => {
  const previewImageUrl = getDGCPreviewImageUrl(digitalGreetingCard);

  return <DGCImageWithRotation alt="digital gift card" src={previewImageUrl} />;
};

const AddToCart = (props: ButtonV3Props) => {
  return (
    <ButtonV3 variant="secondary" {...props}>
      <PlusIcon height={20} width={20} style={{ marginRight: '8px' }} aria-hidden />
      Add
    </ButtonV3>
  );
};

type DGCsUpsellProps = {
  items: CartItemType[];
  updateCart: () => Promise<void>;
  isDgcV5: boolean;
  remove: (id: string) => Promise<void>;
};

/**
 * Prompts a user to add a digital greeting card to their cart.
 */
export const DGCsUpsell = ({ items, updateCart, isDgcV5, remove }: DGCsUpsellProps) => {
  // Double click guard
  const isBusy = useRef(false);

  // Controls a collapse animation when a DGC is selected
  const [selectedDGCUUID, setSelectedDGCUUID] = useState<string | null>();
  const { showDGCs, digitalGreetingCards, digitalGreetingCardProduct, addDGCToCart } = useDGC();
  const [dgcItem, setDgcItem] = useState<CartItemType>();

  const validDigitalGreetingCards: ValidDGC[] = useMemo(() => {
    return digitalGreetingCards?.filter(isValidDGC) || [];
  }, [digitalGreetingCards]);

  const dgcToShow: ValidDGC | undefined = useMemo(() => {
    return validDigitalGreetingCards[0];
  }, [validDigitalGreetingCards]);

  const cashFirstCart = useMemo(() => {
    if (items?.length === 0) return false;
    // Logic if just cash/honeymoon funds - cash funds do not have a brand name
    return !items[0].brandName;
  }, [items]);

  const dgcCartItem = useMemo(() => {
    if (items?.length === 0) return null;
    return items.find((item: CartItemType) => item.brandName === 'Zola Digital Greeting Card');
  }, [items]);

  useEffect(() => {
    if (!items || !digitalGreetingCardProduct) return;
    setDgcItem(items?.find((item) => item?.productId === digitalGreetingCardProduct?.id));
  }, [items, digitalGreetingCardProduct]);

  useEffect(() => {
    if (dgcItem) {
      setSelectedDGCUUID(dgcItem.productLookId);
    } else {
      setSelectedDGCUUID(undefined);
    }
  }, [dgcItem]);

  const handleDGCUpdate = useCallback(
    async (dgcUUID: string) => {
      if (isBusy.current) return;

      setSelectedDGCUUID(dgcUUID);

      isBusy.current = true;
      try {
        if (dgcUUID) {
          const selectedDGC = digitalGreetingCards?.find((dgc) => dgc.id === dgcUUID);
          const dgcSkuId = selectedDGC?.sku_previews?.[0]?.id;
          if (dgcSkuId) await addDGCToCart(dgcSkuId);
          await updateCart();
        }
      } finally {
        isBusy.current = false;
      }
    },
    [digitalGreetingCards, addDGCToCart, updateCart]
  );

  // Both control and test follow this logic
  if (!showDGCs || !dgcToShow) return null;

  // Control - Cash first carts only
  if (!isDgcV5 && !cashFirstCart) return null;

  return (
    <DGCsUpsellContainer isVisible={isDgcV5 ? true : !selectedDGCUUID} isUnderTest={isDgcV5}>
      {isDgcV5 ? (
        <>
          <DGCInfo isUnderTest>
            <DGCCopy dgc={dgcToShow} isDgcV5 />

            <AddToCartContainer>
              {selectedDGCUUID ? (
                <>
                  <AddedToCart>
                    <CheckIcon height={20} width={20} />
                    Added
                  </AddedToCart>
                  <LinkV2 type="button" onClick={() => remove(dgcCartItem?.id || '')} bold>
                    Remove
                  </LinkV2>
                </>
              ) : (
                <AddToCart onClick={() => handleDGCUpdate(dgcToShow.id)} />
              )}
            </AddToCartContainer>
          </DGCInfo>
        </>
      ) : (
        <>
          <DGCInfo isUnderTest={false}>
            <DGCImage digitalGreetingCard={dgcToShow} />
            <div>
              <DGCCopy dgc={dgcToShow} isDgcV5={false} />
              <AddToCart onClick={() => handleDGCUpdate(dgcToShow.id)} css={mobileOnly} />
            </div>
          </DGCInfo>
          <AddToCart onClick={() => handleDGCUpdate(dgcToShow.id)} css={excludeMobile} />
        </>
      )}
    </DGCsUpsellContainer>
  );
};
